import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/States";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { QuickDates, money } from "@/components/sales-reports/EnhancedReports";
import MonthlyStatementTab from "@/components/receivables/MonthlyStatementTab";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Download, Printer, ExternalLink } from "lucide-react";

const STATUS_COLORS = {
  PAID: "bg-emerald-100 text-emerald-700",
  PARTIALLY_PAID: "bg-teal-100 text-teal-700",
  NOT_DUE: "bg-sky-100 text-sky-700",
  DUE: "bg-amber-100 text-amber-700",
  OVERDUE: "bg-rose-100 text-rose-700",
};
const STATUS_LABELS = { PAID: "Paid", PARTIALLY_PAID: "Partially Paid", NOT_DUE: "Not Due", DUE: "Due Today", OVERDUE: "Overdue" };
const BUCKETS = [["notDue", "Not Due"], ["d0_30", "0–30"], ["d31_60", "31–60"], ["d61_90", "61–90"], ["d91_180", "91–180"], ["d180_plus", "180+"]];

function StatusBadge({ v }) {
  return <Badge data-testid={`rd-status-${v}`} className={`${STATUS_COLORS[v] || "bg-stone-100 text-stone-600"} border-0 text-[10px]`}>{STATUS_LABELS[v] || v || "—"}</Badge>;
}

function DaysCell({ r }) {
  if (r.status === "PAID") return <span className="text-stone-400">—</span>;
  if (r.daysOverdue > 0) return <span className="font-semibold text-rose-700">{r.daysOverdue} d overdue</span>;
  if (r.daysOverdue === 0) return <span className="font-semibold text-amber-700">due today</span>;
  return <span className="text-stone-500">in {-r.daysOverdue} d</span>;
}

function Kpi({ label, amount, count, tid, tone }) {
  return (
    <div className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm" data-testid={tid}>
      <div className={`font-display text-lg font-bold ${tone || "text-stone-900"}`}>{money(amount)}</div>
      <div className="mt-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}{count != null ? ` · ${count}` : ""}</div>
    </div>
  );
}

function ExportButtons({ endpoint, qs, testPrefix }) {
  const [busy, setBusy] = useState(false);
  const run = async (fmt) => {
    setBusy(true);
    try {
      const res = await api.get(`${endpoint}${qs}${qs ? "&" : "?"}format=${fmt}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = m ? m[1] : `report.${fmt === "excel" ? "xlsx" : fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported (${fmt.toUpperCase()})`);
    } catch (e) { toast.error(apiErrorMessage(e, "Export failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex gap-1.5">
      <Button data-testid={`${testPrefix}-excel`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("excel")}><Download className="h-3.5 w-3.5" /> Excel</Button>
      <Button data-testid={`${testPrefix}-csv`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("csv")}><Download className="h-3.5 w-3.5" /> CSV</Button>
      <Button data-testid={`${testPrefix}-pdf`} variant="outline" size="sm" className="h-8 gap-1 text-xs" disabled={busy} onClick={() => run("pdf")}><Download className="h-3.5 w-3.5" /> PDF</Button>
      <Button data-testid={`${testPrefix}-print`} variant="outline" size="sm" className="h-8 gap-1 text-xs" onClick={() => window.print()}><Printer className="h-3.5 w-3.5" /> Print</Button>
    </div>
  );
}

function useBuyers() {
  const { data } = useResource("/buyers");
  return Array.isArray(data) ? data : data?.items || [];
}

/* ============================== TAB 1 — RECEIVABLES DUE ============================== */
function ReceivablesDueTab() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [buyerId, setBuyerId] = useState(""); const [status, setStatus] = useState("");
  const [bucket, setBucket] = useState(""); const [search, setSearch] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", buyerId: "", status: "", bucket: "", search: "" });
  const [detail, setDetail] = useState(null);
  const [view, setView] = useState("invoices");

  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, buyerId, status, bucket, search });
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => v && p.set(k, v));
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [applied]);

  const { data, isLoading } = useResource(`/receivables-due${qs}`);
  const buyers = useBuyers();
  const rows = data?.rows || [];
  const s = data?.summary || {};
  const meta = data?.meta || {};
  const byStatus = s.byStatus || {};
  const buckets = s.buckets || {};

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-stone-500" data-testid="rd-date-basis">Date range filters by <b>Due Date</b> (payments becoming due in the period) — not invoice date.</p>
        <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-0.5 text-xs">
          <button data-testid="rd-view-invoices" onClick={() => setView("invoices")} className={`rounded px-3 py-1 font-semibold ${view === "invoices" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Invoice List</button>
          <button data-testid="rd-view-daywise" onClick={() => setView("daywise")} className={`rounded px-3 py-1 font-semibold ${view === "daywise" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Day-wise</button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div>
          <Label className="text-[10px]">Buyer</Label>
          <select data-testid="rd-buyer" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="h-8 w-48 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">All buyers</option>
            {buyers.map((b) => <option key={b.id || b._id} value={b.buyerId || b.id}>{b.parentCompany ? `${b.parentCompany} — ` : ""}{b.name || b.company}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Status</Label>
          <select data-testid="rd-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="h-8 w-32 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">All statuses</option>
            <option value="NOT_DUE">Not Due</option>
            <option value="DUE">Due Today</option>
            <option value="OVERDUE">Overdue</option>
            <option value="PARTIALLY_PAID">Partially Paid</option>
            <option value="PAID">Paid</option>
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Ageing Bucket</Label>
          <select data-testid="rd-bucket" value={bucket} onChange={(e) => setBucket(e.target.value)} className="h-8 w-32 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">All buckets</option>
            {BUCKETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Search</Label>
          <Input data-testid="rd-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Invoice # / buyer / GSTIN" className="h-8 w-48 text-xs" />
        </div>
        <Button size="sm" className="h-8 bg-amber-700 hover:bg-amber-800 text-xs" data-testid="rd-apply" onClick={() => apply()}>Apply</Button>
        <div className="ml-auto"><ExportButtons endpoint="/receivables-due/export" qs={qs} testPrefix="rd-export" /></div>
      </div>

      {isLoading ? <LoadingState label="Loading receivables…" /> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            <Kpi tid="rd-kpi-outstanding" label="Net Outstanding" amount={s.outstanding} count={s.invoices != null ? `${s.invoices} inv` : null} />
            <Kpi tid="rd-kpi-overdue" label="Overdue" amount={byStatus.OVERDUE?.amount} count={byStatus.OVERDUE?.count} tone="text-rose-700" />
            <Kpi tid="rd-kpi-due" label="Due Today" amount={byStatus.DUE?.amount} count={byStatus.DUE?.count} tone="text-amber-700" />
            <Kpi tid="rd-kpi-partial" label="Partially Paid" amount={byStatus.PARTIALLY_PAID?.amount} count={byStatus.PARTIALLY_PAID?.count} tone="text-teal-700" />
            <Kpi tid="rd-kpi-notdue" label="Not Due" amount={byStatus.NOT_DUE?.amount} count={byStatus.NOT_DUE?.count} tone="text-sky-700" />
            <Kpi tid="rd-kpi-paid" label="Paid (settled)" amount={byStatus.PAID?.amount} count={byStatus.PAID?.count} tone="text-emerald-700" />
          </div>

          {view === "daywise" ? (
            (data?.dayWise || []).length === 0 ? <EmptyState title="No due dates" message="No receivables become due in the selected period." /> : (
              <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="rd-daywise-table">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                    <tr><th className="px-3 py-2.5">Due Date</th><th className="px-3 py-2.5 text-right">Invoices Due</th><th className="px-3 py-2.5 text-right">Invoice Value</th><th className="px-3 py-2.5 text-right">Settled</th><th className="px-3 py-2.5 text-right">Net Receivable</th></tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {(data.dayWise).map((d, i) => (
                      <tr key={d.dueDate} data-testid={`rd-day-${i}`} className="hover:bg-stone-50/60">
                        <td className="px-3 py-2.5 font-medium text-stone-800">{d.dueDate}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{d.invoices}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{money(d.invoiceValue)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums text-teal-700">{money(d.settled)}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{money(d.outstanding)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          ) : (<>
          <div className="flex flex-wrap gap-2" data-testid="rd-bucket-strip">
            {BUCKETS.map(([k, l]) => (
              <button key={k} data-testid={`rd-bucket-${k}`} onClick={() => { const nk = bucket === k ? "" : k; setBucket(nk); setApplied({ ...applied, bucket: nk }); }}
                className={`rounded-lg border px-3 py-1.5 text-xs shadow-sm transition-colors ${bucket === k ? "border-amber-700 bg-amber-50 text-amber-800" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}>
                <span className="font-semibold">{l}:</span> {money(buckets[k])}
              </button>
            ))}
          </div>

          <p className="text-xs text-stone-500" data-testid="rd-totals-line">
            Invoice value {money(s.invoiceAmount)} · Credit Notes {money(s.creditNotes)} · Received {money(s.received)} · TDS {money(s.tds)} · Settled {money(s.settled)} · Outstanding {money(s.outstanding)}
            {meta.skippedNoInvoiceDate ? ` · ${meta.skippedNoInvoiceDate} bill(s) skipped (no invoice date)` : ""}
          </p>

          {rows.length === 0 ? (
            <EmptyState title="No receivables" message="No confirmed sales bills match the selected filters." />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="rd-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-3 py-2.5">Invoice #</th>
                    <th className="px-3 py-2.5">Invoice Date</th>
                    <th className="px-3 py-2.5">Due Date</th>
                    <th className="px-3 py-2.5">Days</th>
                    <th className="px-3 py-2.5">Buyer</th>
                    <th className="px-3 py-2.5">GSTIN</th>
                    <th className="px-3 py-2.5 text-right">Invoice Amt</th>
                    <th className="px-3 py-2.5 text-right">Cr. Notes</th>
                    <th className="px-3 py-2.5 text-right">Gross Recv.</th>
                    <th className="px-3 py-2.5 text-right">Received</th>
                    <th className="px-3 py-2.5 text-right">TDS</th>
                    <th className="px-3 py-2.5 text-right">Settled</th>
                    <th className="px-3 py-2.5 text-right">Outstanding</th>
                    <th className="px-3 py-2.5">Bucket</th>
                    <th className="px-3 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r, i) => (
                    <tr key={r.invoiceId} data-testid={`rd-row-${i}`} className="cursor-pointer hover:bg-stone-50/60" onClick={() => setDetail(r)}>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.invoiceNumber}</td>
                      <td className="px-3 py-2.5 text-stone-700">{r.invoiceDate}</td>
                      <td className="px-3 py-2.5 text-stone-700">{r.dueDate}</td>
                      <td className="px-3 py-2.5"><DaysCell r={r} /></td>
                      <td className="px-3 py-2.5 text-stone-700">
                        <div className="font-medium">{r.parentCompany}</div>
                        {r.store && r.store !== r.parentCompany && <div className="text-xs text-stone-500">{r.store}</div>}
                      </td>
                      <td className="px-3 py-2.5 font-mono text-xs text-stone-600">{r.gstin || "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.invoiceAmount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.creditNotes)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.grossReceivable)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.received)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{r.tds ? money(r.tds) : "—"}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.totalSettled)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold">{money(r.outstanding)}</td>
                      <td className="px-3 py-2.5 text-stone-600">{r.bucketLabel}</td>
                      <td className="px-3 py-2.5"><StatusBadge v={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </>)}
        </>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="rd-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-mono text-base">{detail?.invoiceNumber}</DialogTitle>
            <DialogDescription>Invoice-level receivable detail</DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                <div><div className="text-[10px] uppercase text-stone-500">Buyer</div><div className="font-medium" data-testid="rd-detail-buyer">{detail.parentCompany}{detail.store && detail.store !== detail.parentCompany ? ` — ${detail.store}` : ""}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">GSTIN</div><div className="font-mono text-xs">{detail.gstin || "—"}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Invoice Date</div><div data-testid="rd-detail-invdate">{detail.invoiceDate}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Due Date</div><div data-testid="rd-detail-duedate">{detail.dueDate} <span className="text-xs text-stone-500">({detail.termsSource === "BUYER" ? `buyer terms ${detail.paymentTermsDays}d` : `default ${detail.paymentTermsDays}d`})</span></div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Status</div><StatusBadge v={detail.status} /></div>
                <div><div className="text-[10px] uppercase text-stone-500">Ageing Bucket</div><div>{detail.bucketLabel}</div></div>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-lg border border-stone-200 bg-stone-50 p-3">
                <div><div className="text-[10px] uppercase text-stone-500">Invoice Amount</div><div className="font-semibold">{money(detail.invoiceAmount)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Credit Notes</div><div>{money(detail.creditNotes)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Gross Receivable</div><div>{money(detail.grossReceivable)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Cash/Bank Received</div><div>{money(detail.received)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">TDS Deducted</div><div data-testid="rd-detail-tds">{money(detail.tds)}</div></div>
                <div><div className="text-[10px] uppercase text-stone-500">Total Settled</div><div>{money(detail.totalSettled)}</div></div>
                <div className="col-span-2"><div className="text-[10px] uppercase text-stone-500">Outstanding</div><div className="font-semibold text-amber-800" data-testid="rd-detail-outstanding">{money(detail.outstanding)}</div></div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-stone-500">Source: {detail.source} · Bill status: {detail.billStatus}</div>
                <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="rd-detail-open-bill" onClick={() => navigate(`/sales-bills/${detail.invoiceId}`)}>
                  <ExternalLink className="h-3.5 w-3.5" /> Open Sales Bill
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================== TAB 2 — TDS REPORT ============================== */
function TdsReportTab() {
  const [from, setFrom] = useState(""); const [to, setTo] = useState("");
  const [buyerId, setBuyerId] = useState(""); const [invoice, setInvoice] = useState(""); const [tdsRate, setTdsRate] = useState("");
  const [applied, setApplied] = useState({ dateFrom: "", dateTo: "", buyerId: "", invoice: "", tdsRate: "" });

  const apply = (f = from, t = to) => setApplied({ dateFrom: f, dateTo: t, buyerId, invoice, tdsRate });
  const qs = useMemo(() => {
    const p = new URLSearchParams();
    Object.entries(applied).forEach(([k, v]) => v && p.set(k, v));
    const s = p.toString();
    return s ? `?${s}` : "";
  }, [applied]);

  const { data, isLoading } = useResource(`/tds-report${qs}`);
  const buyers = useBuyers();
  const rows = data?.rows || [];
  const s = data?.summary || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
        <QuickDates from={from} to={to} setFrom={setFrom} setTo={setTo} onApply={apply} />
        <div>
          <Label className="text-[10px]">Buyer</Label>
          <select data-testid="tdsr-buyer" value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="h-8 w-48 rounded-md border border-stone-200 bg-white px-2 text-xs">
            <option value="">All buyers</option>
            {buyers.map((b) => <option key={b.id || b._id} value={b.buyerId || b.id}>{b.name || b.company}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-[10px]">Invoice #</Label>
          <Input data-testid="tdsr-invoice" value={invoice} onChange={(e) => setInvoice(e.target.value)} placeholder="Invoice no" className="h-8 w-36 text-xs" />
        </div>
        <div>
          <Label className="text-[10px]">TDS Rate %</Label>
          <Input data-testid="tdsr-rate" type="number" value={tdsRate} onChange={(e) => setTdsRate(e.target.value)} placeholder="e.g. 0.01" className="h-8 w-24 text-xs" />
        </div>
        <Button size="sm" className="h-8 bg-amber-700 hover:bg-amber-800 text-xs" data-testid="tdsr-apply" onClick={() => apply()}>Apply</Button>
        <div className="ml-auto"><ExportButtons endpoint="/tds-report/export" qs={qs} testPrefix="tdsr-export" /></div>
      </div>

      {isLoading ? <LoadingState label="Loading TDS report…" /> : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi tid="tdsr-kpi-total" label="Total TDS Deducted" amount={s.totalTds} tone="text-amber-700" />
            <Kpi tid="tdsr-kpi-entries" label="Entries" amount={s.entries} />
            <Kpi tid="tdsr-kpi-net" label="Net Bank/Cash Receipts" amount={s.netReceipts} />
            <Kpi tid="tdsr-kpi-settled" label="Total Settled (incl. TDS)" amount={s.totalSettled} />
          </div>

          {rows.length === 0 ? (
            <EmptyState title="No TDS entries" message="No buyer-deducted TDS recorded in the selected period." />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-sm" data-testid="tdsr-table">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wide text-stone-500">
                  <tr>
                    <th className="px-3 py-2.5">Date</th>
                    <th className="px-3 py-2.5">Buyer</th>
                    <th className="px-3 py-2.5">Invoice</th>
                    <th className="px-3 py-2.5 text-right">Invoice Amount</th>
                    <th className="px-3 py-2.5 text-right">TDS Rate %</th>
                    <th className="px-3 py-2.5 text-right">TDS Amount</th>
                    <th className="px-3 py-2.5 text-right">Net Receipt</th>
                    <th className="px-3 py-2.5">Receipt Ref</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {rows.map((r, i) => (
                    <tr key={`${r.receiptId}-${r.invoiceId}`} data-testid={`tdsr-row-${i}`} className="hover:bg-stone-50/60">
                      <td className="px-3 py-2.5 text-stone-700">{r.date}</td>
                      <td className="px-3 py-2.5 text-stone-700">{r.buyerName}</td>
                      <td className="px-3 py-2.5 font-mono text-xs">{r.invoiceNo}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.invoiceAmount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{r.tdsRate}%</td>
                      <td className="px-3 py-2.5 text-right tabular-nums font-semibold text-amber-700">{money(r.tdsAmount)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{money(r.netReceipt)}</td>
                      <td className="px-3 py-2.5 text-xs text-stone-500"><span className="font-mono">{r.receiptNo}</span>{r.reference ? ` · ${r.reference}` : ""}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-stone-50 font-semibold">
                  <tr>
                    <td className="px-3 py-2" colSpan={5}>TOTAL</td>
                    <td className="px-3 py-2 text-right tabular-nums text-amber-700" data-testid="tdsr-total">{money(s.totalTds)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{money(s.netReceipts)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function ReceivablesDue() {
  const [tab, setTab] = useState("due");
  return (
    <div className="space-y-6" data-testid="receivables-due-page">
      <PageHeader title="Receivables Due" subtitle="Due date = Invoice Date + buyer payment terms (default 30 days) · confirmed sales bills · TDS settles the receivable but is not bank money" />
      <div className="flex gap-1 rounded-lg border border-stone-200 bg-white p-1 shadow-sm w-fit">
        <button data-testid="rd-tab-due" onClick={() => setTab("due")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === "due" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Receivables Due</button>
        <button data-testid="rd-tab-tds" onClick={() => setTab("tds")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === "tds" ? "bg-amber-700 text-white" : "text-stone-600"}`}>TDS Report</button>
        <button data-testid="rd-tab-statement" onClick={() => setTab("statement")} className={`rounded-md px-4 py-1.5 text-sm font-semibold ${tab === "statement" ? "bg-amber-700 text-white" : "text-stone-600"}`}>Monthly Statement</button>
      </div>
      {tab === "due" ? <ReceivablesDueTab /> : tab === "tds" ? <TdsReportTab /> : <MonthlyStatementTab />}
    </div>
  );
}
